// src/hooks/useSSE.js
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * @param {Object} opts
 * @param {string} opts.url               URL del endpoint SSE (GET)
 * @param {Record<string,string>=} opts.headers Headers extra (ej. Authorization)
 * @param {(data:any)=>void=} opts.onMessage  Handler por cada "data:" recibido
 * @param {(err:any)=>void=} opts.onError     Handler de error
 * @param {()=>void=} opts.onOpen             Llamado al conectar
 * @param {number=} opts.minDelayMs           Backoff mínimo (ms)
 * @param {number=} opts.maxDelayMs           Backoff máximo (ms)
 * @param {boolean=} opts.enabled             Si false, no conecta
 */
export function useSSE(opts) {
  const {
    url,
    headers,
    onMessage,
    onError,
    onOpen,
    minDelayMs = 1000,
    maxDelayMs = 15000,
    enabled = true,
  } = opts || {};

  const [connected, setConnected] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const abortRef = useRef(null);
  const pausedRef = useRef(false);

  const pause = useCallback(() => {
    pausedRef.current = true;
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const resume = useCallback(() => {
    pausedRef.current = false;
    setAttempt((a) => a + 1); // reintenta
  }, []);

  useEffect(() => {
    if (!enabled || pausedRef.current) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const run = async () => {
      try {
        const res = await fetch(url, {
          method: "GET",
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(`SSE HTTP ${res.status}`);

        setConnected(true);
        if (onOpen) onOpen();

        const reader =
          res.body && res.body.getReader ? res.body.getReader() : null;
        if (!reader)
          throw new Error("ReadableStream no soportado en este navegador");

        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          if (!value) continue;

          buffer += decoder.decode(value, { stream: true });

          // Procesa eventos delimitados por "\n\n"
          let idx;
          // eslint-disable-next-line no-cond-assign
          while ((idx = buffer.indexOf("\n\n")) !== -1) {
            const rawEvent = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);

            // Ignora comentarios ":" y líneas vacías
            const lines = rawEvent
              .split("\n")
              .filter((l) => l && !l.startsWith(":"));
            for (const l of lines) {
              const m = l.match(/^data:\s*(.*)$/);
              if (!m) continue;
              const payload = m[1];
              try {
                const parsed = JSON.parse(payload);
                if (onMessage) onMessage(parsed);
              } catch {
                if (onMessage) onMessage(payload);
              }
            }
          }
        }
      } catch (e) {
        if (controller.signal.aborted) return; // pausado o desmontado
        if (onError) onError(e);
        setConnected(false);
        const delay = Math.min(
          maxDelayMs,
          Math.round(minDelayMs * Math.pow(2, attempt || 0))
        );
        setTimeout(() => setAttempt((a) => a + 1), delay);
      }
    };

    run();

    return () => {
      controller.abort();
      setConnected(false);
    };
    // Nota: si 'headers' es un objeto, podrías memorizarlo con useMemo en el caller
    // para evitar reconexiones innecesarias.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled, attempt, minDelayMs, maxDelayMs, headers]);

  return { connected, pause, resume };
}
